import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fileSrc } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Paperclip, Send, Image as ImageIcon, X, MessageSquare, ArrowLeft, Heart, Reply } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { playChime } from "@/lib/sound";
import { formatMessageTime } from "@/lib/time";

function AttachmentView({ fileId, mine }) {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    api.get(`/files/${fileId}`).then(({ data }) => setMeta(data)).catch(() => {});
  }, [fileId]);
  if (!meta) return null;
  if (meta.is_image) {
    return <img src={fileSrc(fileId)} alt={meta.original_filename} className="mt-2 max-h-60 rounded-xl" />;
  }
  return (
    <a href={fileSrc(fileId)} target="_blank" rel="noreferrer"
       className={`mt-2 flex items-center gap-2 px-3 py-2 rounded-xl ${mine ? "bg-purple-900/40" : "bg-white"} text-sm`}>
      <Paperclip className="w-4 h-4" /><span className="truncate">{meta.original_filename}</span>
    </a>
  );
}

export default function Messages() {
  const { user } = useAuth();
  const readOnly = !!user?.read_only;
  const [params, setParams] = useSearchParams();
  const activeId = params.get("to");
  const [conversations, setConversations] = useState([]);
  const [other, setOther] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const initialScrollRef = useRef(false);
  const prevUnreadRef = useRef(null);

  const loadConversations = useCallback(() => {
    api.get("/dm/conversations").then(({ data }) => {
      setConversations(data);
      const total = data.reduce((s, c) => s + (c.unread || 0), 0);
      if (prevUnreadRef.current !== null && total > prevUnreadRef.current) playChime();
      prevUnreadRef.current = total;
    }).catch(() => {});
  }, []);

  const loadThread = useCallback(() => {
    if (!activeId) { setOther(null); setMessages([]); return; }
    api.get(`/dm/${activeId}`).then(({ data }) => {
      setOther(data.other);
      setMessages(data.messages);
    }).catch(() => toast.error("Could not open conversation"));
  }, [activeId]);

  useEffect(() => { loadConversations(); }, [loadConversations]);
  useEffect(() => { loadThread(); }, [loadThread]);

  useEffect(() => {
    const t = setInterval(() => { loadConversations(); loadThread(); }, 1500);
    return () => clearInterval(t);
  }, [loadConversations, loadThread]);

  useEffect(() => { initialScrollRef.current = false; }, [activeId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      if (!initialScrollRef.current) {
        el.scrollTop = el.scrollHeight;
        initialScrollRef.current = true;
      } else {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      }
    });
  }, [messages]);

  const openConversation = (uid) => setParams({ to: uid });

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachment(data);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !attachment) return;
    try {
      const { data } = await api.post(`/dm/${activeId}`, {
        text: text.trim(),
        attachment_file_id: attachment?.id || null,
        reply_to: replyTo?.id || null,
      });
      setMessages((prev) => [...prev, data]);
      setText(""); setAttachment(null); setReplyTo(null);
      loadConversations();
    } catch (er) {
      toast.error(er.response?.data?.detail || "Send failed");
    }
  };

  const toggleLike = async (id) => {
    setMessages((prev) => prev.map((m) => {
      if (m.id !== id) return m;
      const liked = (m.liked_by || []).includes(user?.user_id);
      const liked_by = liked
        ? (m.liked_by || []).filter((u) => u !== user?.user_id)
        : [...(m.liked_by || []), user?.user_id];
      return { ...m, liked_by };
    }));
    try {
      await api.post(`/dm/message/${id}/like`);
    } catch {
      toast.error("Failed to like");
    }
  };

  const startReply = (m) => {
    setReplyTo({ id: m.id, author_name: m.sender_name || other?.name, text: m.text });
    inputRef.current?.focus();
  };

  return (
    <div className="animate-fade-up" data-testid="messages-page">
      <div className="mb-4">
        <p className="text-xs uppercase tracking-[0.2em] text-purple-700 font-bold">Private</p>
        <h1 className="text-4xl sm:text-5xl font-black font-display tracking-tight">Messages</h1>
      </div>

      <div className="flex h-[calc(100vh-13rem)] bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
        {/* Conversation list */}
        <aside className={`w-full md:w-80 border-r border-slate-100 flex-col ${activeId ? "hidden md:flex" : "flex"}`} data-testid="dm-conversation-list">
          <div className="px-4 py-3 border-b border-slate-100">
            <h2 className="font-black text-slate-900">Conversations</h2>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 && (
              <div className="text-center text-slate-400 py-10 px-4">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">No conversations yet. Start one from the Members tab.</p>
              </div>
            )}
            {conversations.map((c) => (
              <button
                key={c.conversation_id}
                onClick={() => openConversation(c.other_user_id)}
                data-testid={`dm-conv-${c.other_user_id}`}
                className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left ${activeId === c.other_user_id ? "bg-purple-50" : ""}`}
              >
                <Avatar className="w-11 h-11 shrink-0">
                  <AvatarImage src={c.other_picture} />
                  <AvatarFallback className="bg-purple-700 text-white font-bold">{c.other_name?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-slate-900 truncate text-sm">{c.other_name}</p>
                  <p className="text-xs text-slate-500 truncate">{c.last_sender_id === user?.user_id ? "You: " : ""}{c.last_text}</p>
                </div>
                {c.unread > 0 && (
                  <span data-testid={`dm-unread-${c.other_user_id}`} className="shrink-0 min-w-5 h-5 px-1.5 rounded-full bg-amber-500 text-slate-900 text-[11px] font-black flex items-center justify-center">{c.unread}</span>
                )}
              </button>
            ))}
          </div>
        </aside>

        {/* Thread */}
        <section className={`relative flex-1 flex-col ${activeId ? "flex" : "hidden md:flex"}`}>
          <div aria-hidden className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center">
            <img src="/cfaa-mark.png" alt="" className="w-full h-full opacity-100 object-cover" />
          </div>
          {!activeId ? (
            <div className="relative z-10 flex-1 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <MessageSquare className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="font-semibold">Select a conversation</p>
              </div>
            </div>
          ) : (
            <>
              <div className="relative z-10 px-4 py-3 border-b border-slate-100 flex items-center gap-3">
                <button onClick={() => setParams({})} className="md:hidden text-slate-500" data-testid="dm-back-btn">
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <Avatar className="w-9 h-9">
                  <AvatarImage src={other?.picture} />
                  <AvatarFallback className="bg-purple-700 text-white font-bold text-sm">{other?.name?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <h3 className="font-black text-slate-900" data-testid="dm-thread-title">{other?.name}</h3>
              </div>

              <div ref={scrollRef} className="relative z-10 flex-1 overflow-y-auto chat-scroll p-6 space-y-4" data-testid="dm-message-list">
                {messages.length === 0 && (
                  <div className="text-center text-slate-400 py-12">
                    <p className="text-lg font-semibold">No messages yet — say hello 👋</p>
                  </div>
                )}
                {messages.map((m) => {
                  const mine = m.sender_id === user?.user_id;
                  const likeCount = (m.liked_by || []).length;
                  const liked = (m.liked_by || []).includes(user?.user_id);
                  return (
                    <div key={m.id} className={`group flex gap-3 animate-slide-in ${mine ? "flex-row-reverse" : ""}`}>
                      <div className={`max-w-[75%] flex flex-col ${mine ? "items-end" : "items-start"}`}>
                        <div className={`px-4 py-2.5 rounded-2xl ${mine ? "bg-purple-700 text-white rounded-tr-sm" : "bg-slate-100 text-slate-900 rounded-tl-sm"}`}>
                          {m.reply_to && (
                            <div className={`mb-2 pl-2 border-l-2 rounded-sm text-xs py-1 pr-2 ${mine ? "border-white/50 bg-white/10" : "border-purple-400 bg-purple-50"}`}>
                              <div className={`font-bold ${mine ? "text-white/90" : "text-purple-700"}`}>{m.reply_to.author_name}</div>
                              <div className={`truncate ${mine ? "text-white/80" : "text-slate-600"}`}>{m.reply_to.text}</div>
                            </div>
                          )}
                          {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
                          {m.attachment_file_id && <AttachmentView fileId={m.attachment_file_id} mine={mine} />}
                        </div>
                        <div className={`flex items-center gap-3 mt-1 px-1 ${mine ? "flex-row-reverse" : ""}`}>
                          <button
                            type="button"
                            onClick={() => toggleLike(m.id)}
                            data-testid={`dm-like-${m.id}`}
                            className={`inline-flex items-center gap-1 text-xs font-semibold transition-colors ${liked ? "text-red-500" : "text-slate-400 hover:text-red-500"}`}
                          >
                            <Heart className={`w-3.5 h-3.5 ${liked ? "fill-red-500" : ""}`} />
                            {likeCount > 0 && likeCount}
                          </button>
                          <button
                            type="button"
                            onClick={() => startReply(m)}
                            data-testid={`dm-reply-${m.id}`}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-purple-700 transition-colors opacity-0 group-hover:opacity-100"
                          >
                            <Reply className="w-3.5 h-3.5" /> Reply
                          </button>
                          <span
                            data-testid={`dm-timestamp-${m.id}`}
                            title={new Date(m.created_at).toLocaleString()}
                            className="text-[11px] text-slate-400 font-medium"
                          >
                            {formatMessageTime(m.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <form onSubmit={send} className="relative z-10 border-t border-slate-100 p-4 space-y-2">
                {replyTo && (
                  <div className="flex items-center gap-2 bg-purple-50 rounded-2xl px-3 py-2 text-sm border-l-4 border-purple-400" data-testid="dm-reply-banner">
                    <Reply className="w-4 h-4 text-purple-700 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold text-purple-700">Replying to {replyTo.author_name}</div>
                      <div className="truncate text-slate-600 text-xs">{replyTo.text || "attachment"}</div>
                    </div>
                    <button type="button" onClick={() => setReplyTo(null)} data-testid="dm-cancel-reply" className="text-slate-400 hover:text-slate-700">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {attachment && (
                  <div className="flex items-center gap-2 bg-purple-50 rounded-2xl px-3 py-2 text-sm">
                    <Paperclip className="w-4 h-4 text-purple-700" />
                    <span className="truncate flex-1">{attachment.original_filename}</span>
                    <button type="button" onClick={() => setAttachment(null)} className="text-slate-400 hover:text-slate-700" data-testid="dm-remove-attachment-btn">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input ref={fileRef} type="file" className="hidden" onChange={handleFile} data-testid="dm-file-input" />
                  <Button type="button" variant="ghost" size="icon" onClick={() => fileRef.current?.click()} disabled={uploading || readOnly} data-testid="dm-attach-btn" className="rounded-full">
                    <ImageIcon className="w-5 h-5 text-slate-500" />
                  </Button>
                  <Input
                    ref={inputRef}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={readOnly ? "Read-only — pay your dues to message" : "Type a message…"}
                    disabled={readOnly}
                    data-testid="dm-text-input"
                    className="flex-1 h-12 rounded-2xl border-slate-200 disabled:opacity-60 disabled:cursor-not-allowed"
                  />
                  <Button type="submit" disabled={readOnly} data-testid="dm-send-btn" className="h-12 px-5 rounded-2xl bg-purple-700 hover:bg-purple-800 text-white btn-lift disabled:opacity-50">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
